import React, { useMemo, useState } from "react";

import { Container } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useGetFartherManagedAccounts";
import useClient from "@fartherfinance/frontend/api/EntityData/hooks/useClient";
import useFindPortfolioPlanAssignment from "@fartherfinance/frontend/api/Investment/hooks/useFindPortfolioPlanAssignment";
import {
  Filters,
  Pagination,
} from "@fartherfinance/frontend/api/Investment/request/findPortfolioPlanAssignment";
import usePostRaiseCash from "@fartherfinance/frontend/api/Liquidations/hooks/usePostRaiseCash";
import {
  ClientId,
  FartherAccountId,
  PortfolioPlanAssignmentId,
  TaskId,
} from "@fartherfinance/frontend/api/Types";

import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import { handleApiError } from "@src/utils/handleApiError";

import RaiseCashDetails from "./RaiseCashDetails";
import RaiseCashForm from "./RaiseCashForm";

const infinitePagination: Pagination = {
  page: 1,
  pageSize: 100,
  orderBy: "createdAt desc",
};

type LiquidationFlowStep = "RequestForm" | "ConfirmationDetails";

interface RequestData {
  concretePortfolioId: PortfolioPlanAssignmentId;
  accountId: FartherAccountId;
  accountName: string;
  custodianAccountId: string;
  amount: string;
  isFullLiquidation: boolean;
}

const RaiseCash = (): JSX.Element => {
  const [liquidationStep, setLiquidationStep] =
    useState<LiquidationFlowStep>("RequestForm");
  const [isMutating, setIsMutating] = useState<boolean>(false);

  const [requestData, setRequestData] = useState<RequestData | undefined>(
    undefined
  );

  const { clientId } = useParams<{
    clientId: ClientId;
  }>();

  const navigate = useNavigate();

  const notify = useStatusNotification();

  const auth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();
  const client = useClient(clientId, auth);
  const managedAccounts = useGetFartherManagedAccounts(clientId, auth);
  const postRaiseCash = usePostRaiseCash(advisorAuth);

  const getAssignmentsFilters: Filters = useMemo(
    (): Filters => ({ clientId }),
    [clientId]
  );

  const accountAssignments = useFindPortfolioPlanAssignment(
    getAssignmentsFilters,
    infinitePagination,
    auth
  );

  const accounts = useMemo(
    () =>
      (managedAccounts.data ?? []).filter((a) =>
        a.accountOwner.includes(clientId)
      ),
    [managedAccounts.data, clientId]
  );

  const assignments = useMemo(
    () => accountAssignments.data?.data ?? [],
    [accountAssignments.data]
  );

  const filteredAccounts = useMemo(() => {
    if (accounts.length === 0) {
      return [];
    }

    if (assignments.length === undefined) {
      return [];
    }

    const activeAssignments = assignments.filter((a) => a.status === "Active");

    const validAccounts = activeAssignments.flatMap((a) => a.accounts);

    return accounts.filter((a) => validAccounts.includes(a.virtualAccountId));
  }, [accounts, assignments]);

  const handleRedirectToDashboard = () => {
    navigate(`/Client/${clientId}/Dashboard`);
  };

  const handleRedirectToTaskDetails = (taskId: TaskId) => {
    if (taskId) {
      // NOTE: first redirect stops the impersonation, second redirect us where we want to go
      // we cannot freely redirect between /Client and /Advisor routes due to the impersonation logic
      setTimeout(() => {
        navigate("/Advisor");
      }, 1200);

      setTimeout(() => {
        navigate(`/Advisor/Tasks/${taskId}`);
      }, 1500);
    }
  };

  const handleMoveToStep = (step: LiquidationFlowStep) => {
    setLiquidationStep(step);
  };

  const handleRaiseCashFormContinue = (
    accountId: FartherAccountId,
    amount: string,
    isFullLiquidation: boolean
  ) => {
    const account = filteredAccounts.find(
      (a) => a.virtualAccountId === accountId
    );
    if (account === undefined) {
      throw new Error(`Account ${accountId} not found`);
    }

    if (account.accountDetails.custodianAccountNumber === null) {
      throw new Error(
        `Account ${accountId} does not have a custodian account number`
      );
    }

    const cip = assignments.find((a) => a.accounts.includes(accountId));
    if (cip === undefined) {
      throw new Error("Assignment not found");
    }

    setRequestData({
      concretePortfolioId: cip.planAssignmentId,
      accountId: accountId,
      accountName: account.accountDetails.displayName,
      custodianAccountId: account.accountDetails.custodianAccountNumber,
      amount: removeCommas(amount),
      isFullLiquidation,
    });

    setLiquidationStep("ConfirmationDetails");
  };

  const handleSubmit = async () => {
    if (requestData === undefined) {
      return;
    }

    const advisorId = client.data?.advisorId;
    if (advisorId === undefined) {
      return;
    }

    setIsMutating(true);

    try {
      const { concretePortfolioId, ...rest } = requestData;

      const res = await postRaiseCash({
        planAssignmentId: concretePortfolioId,
        body: {
          clientId,
          advisorId,
          ...rest,
        },
      });

      notify("Raise cash request created", "Success");

      handleRedirectToTaskDetails(res.data.taskId);
    } catch (error) {
      handleApiError({
        error,
        errorMessage: "Error creating raise cash request.",
        genericErrorMessage: "Error creating raise cash request",
        notify,
      });
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <BaseLayout>
      {(isMutating ||
        managedAccounts.isLoading ||
        accountAssignments.isLoading) && <LogoLoadingStill onTop />}

      <PageHero start={<TitleBlock title="Raise Cash" />} />

      <Container maxWidth="md" sx={{ mt: "48px" }}>
        {liquidationStep === "RequestForm" && (
          <RaiseCashForm
            accounts={filteredAccounts}
            onClose={handleRedirectToDashboard}
            onContinue={handleRaiseCashFormContinue}
          />
        )}

        {liquidationStep === "ConfirmationDetails" &&
          requestData !== undefined && (
            <RaiseCashDetails
              accountName={requestData.accountName}
              custodianAccountId={requestData.custodianAccountId}
              amount={requestData.amount}
              isFullLiquidation={requestData.isFullLiquidation}
              onContinue={handleSubmit}
              onClose={() => handleMoveToStep("RequestForm")}
            />
          )}
      </Container>
    </BaseLayout>
  );
};

export default RaiseCash;
